import { useCallback } from 'react';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { useAppSelector } from 'src/utils/Hooks';
import { prefillFormData } from 'src/utils/segments';
import { getQueryParamValue } from 'src/utils/app';

export default function useAtlasV2KeywordSearchRequestBuilder() {
  const queryParams = useAppSelector((state) => state.app.queryParams);

  const formData = prefillFormData({ queryParams, appName: 'atlas' });
  const { termFilters, rangeFilters } = formData;

  const activeTermFilters = Object.entries(termFilters)
    .filter(
      ([_, filter]) =>
        (Array.isArray(filter.values) && filter.values.length > 0) || Object.values(filter.values).length > 0
    )
    .map(([fieldName, filter]) => ({
      fieldName,
      condition: filter.condition,
      values: Array.isArray(filter.values)
        ? filter.values.map(({ i }) => i)
        : Object.values(filter.values).map(({ i }) => i)
    }));

  const activeRangeFilters = Object.entries(rangeFilters).map(([fieldName, filter]) => ({
    fieldName,
    minValue: filter.minValue,
    maxValue: filter.maxValue
  }));

  return useCallback(
    (additionalBuilder?: (builder: AdvancedSearchRequestBuilder) => AdvancedSearchRequestBuilder) =>
      (requestBuilder: AdvancedSearchRequestBuilder) => {
        const req = requestBuilder.clone().build();
        const stacklineFilter = req.conditions.termFilters.find((filter) => filter.fieldName === 'stacklineSku');

        const existingKeywordFilter = req.conditions.termFilters.find((filter) => filter.fieldName === 'keywordPhrase');

        if (getQueryParamValue('searchPage') === 'true') {
          requestBuilder.removeConditionTermFilters((field) => field.fieldName === 'categoryId');
          requestBuilder.removeConditionTermFilters((field) => field.fieldName === 'subCategoryId');
          requestBuilder.removeConditionTermFilters((field) => field.fieldName === 'brandId');
        }

        // We don't want to see search results for the Other Traffic keyword
        requestBuilder
          .removeConditionTermFilters(
            (filter) => filter.fieldName === 'excludedSearchTerm' && filter.values.includes('[Other Traffic]')
          )
          .addValueToConditionTermFilter('keywordPhrase', [
            ...(stacklineFilter?.values || []),
            ...(existingKeywordFilter?.values || [])
          ])
          .removeConditionTermFilters((field) => field.fieldName === 'stacklineSku')
          .removeConditionTermFilters((field) => field.fieldName === 'brandId')

          .addConditionTermFilter('excludedSearchTerm', 'must_not', ['[Other Traffic]', '[other traffic]']);

        activeTermFilters.forEach(({ fieldName, condition, values }) => {
          requestBuilder.addConditionTermFilter(fieldName, condition, values);
        });

        activeRangeFilters.forEach(({ fieldName, minValue, maxValue }) => {
          requestBuilder.addConditionRangeFilter(fieldName, minValue, maxValue);
        });

        if (additionalBuilder) {
          return additionalBuilder(requestBuilder);
        }

        return requestBuilder;
      },
    [activeRangeFilters, activeTermFilters]
  );
}
